import { AlertTriangle, ChefHat, Info } from 'lucide-react';

import FormModal from '../../../components/Modals/FormModal';
import type { RestaurantRecipeAvailability } from '../../../types/restaurant/RestaurantProductionInventoryTypes';

interface Props {
  availability: RestaurantRecipeAvailability | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const fmt = (value: string | number) =>
  Number(value ?? 0).toLocaleString('es-SV', { maximumFractionDigits: 4 });

/**
 * Aviso informativo (no bloquea) que se muestra en el POS al vender un producto
 * con receta cuando la sucursal tiene activo "Validar existencia antes de
 * preparar". Enumera los insumos y advierte cuáles quedarán en negativo.
 */
const RecipeStockInfoModal = ({ availability, onConfirm, onCancel }: Props) => {
  if (!availability) return null;

  const shortages = availability.ingredients.filter((i) => !i.sufficient);
  const hasShortage = shortages.length > 0;

  return (
    <FormModal
      isOpen
      onClose={onCancel}
      title="Consumo de materia prima"
      maxWidth="max-w-lg"
    >
      <div className="space-y-4">
        <div
          className={`flex items-start gap-3 rounded-xl border p-4 text-sm ${
            hasShortage
              ? 'border-warning/30 bg-warning/5 text-black dark:text-white'
              : 'border-primary/20 bg-primary/5 text-black dark:text-white'
          }`}
        >
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
              hasShortage
                ? 'bg-warning/15 text-warning'
                : 'bg-primary/10 text-primary'
            }`}
          >
            {hasShortage ? (
              <AlertTriangle className="h-5 w-5" />
            ) : (
              <Info className="h-5 w-5" />
            )}
          </span>

          <div>
            <p className="font-medium">
              {availability.recipeName || 'Producto con receta'}
            </p>
            <p className="mt-1 text-body dark:text-bodydark2">
              {hasShortage
                ? 'Este producto descuenta materia prima y algunos insumos no alcanzan. La venta continúa y el inventario quedará en negativo.'
                : 'Este producto descuenta materia prima de la receta al venderlo. Hay existencias suficientes.'}
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-stroke dark:border-strokedark">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stroke bg-gray-2/60 text-left text-xs font-semibold uppercase tracking-wide text-bodydark2 dark:border-strokedark dark:bg-meta-4/30">
                <th className="px-3 py-2.5">Insumo</th>
                <th className="px-3 py-2.5 text-right">Requerido</th>
                <th className="px-3 py-2.5 text-right">Existencia</th>
              </tr>
            </thead>
            <tbody>
              {availability.ingredients.map((ing) => (
                <tr
                  key={ing.rawMaterialId}
                  className="border-b border-stroke last:border-0 dark:border-strokedark"
                >
                  <td className="px-3 py-2.5 text-black dark:text-white">
                    {ing.rawMaterialName}
                  </td>
                  <td className="px-3 py-2.5 text-right text-bodydark2">
                    {fmt(ing.requiredBaseQuantity)} {ing.baseUnitCode}
                  </td>
                  <td
                    className={`px-3 py-2.5 text-right font-medium ${
                      ing.sufficient
                        ? 'text-success'
                        : 'text-meta-8 dark:text-warning'
                    }`}
                  >
                    {fmt(ing.currentStock)} {ing.baseUnitCode}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-4 dark:border-strokedark sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-stroke px-5 py-2.5 text-sm font-medium dark:border-strokedark"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white"
          >
            <ChefHat className="h-4 w-4" />
            Agregar de todas formas
          </button>
        </div>
      </div>
    </FormModal>
  );
};

export default RecipeStockInfoModal;
